import { useState } from "react";
import { toast } from "@/lib/toast";
import { LockIcon as LockIcon, ShieldCheck, ShieldOff, KeyRound, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import DeskConfirm from "@/components/crm/DeskConfirm";

/**
 * SecuritySection — Paramètres → Sécurité (spec 2026-02-18).
 *
 * • Configure or rotate the 4-digit lock PIN.
 * • Remove the PIN (with re-authentication).
 * • Displays a friendly banner when the PIN is not yet set — inviting the
 *   user to activate the auto-lock without ever blocking normal usage.
 */
export default function SecuritySection() {
  const { user, refresh } = useAuth();
  const hasPin = !!user?.has_lock_pin;

  const [setOpen, setSetOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);

  return (
    <Card className="bg-card border-vertec-borderDark p-8" data-testid="security-section">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold mb-1 flex items-center gap-2">
          <LockIcon size={20} className="text-vertec-blue" /> Sécurité — Code PIN
        </h2>
        <p className="text-muted-foreground text-sm">
          Le code PIN à 4 chiffres verrouille automatiquement l&apos;application
          après <strong>10 minutes d&apos;inactivité</strong> ou lorsque vous cliquez
          sur <strong>« Bye bye »</strong>. Vous restez connecté(e), mais un
          écran de saisie protège l&apos;accès jusqu&apos;à ce que vous entriez
          votre PIN.
        </p>
      </div>

      {!hasPin && (
        <div
          className="mb-6 p-4 rounded-lg border flex items-start gap-3"
          style={{
            borderColor: "rgba(245,200,66,0.35)",
            background: "rgba(245,200,66,0.08)",
          }}
          data-testid="pin-banner"
        >
          <AlertTriangle size={18} style={{ color: "#F5C842", flexShrink: 0, marginTop: 2 }} />
          <div className="flex-1">
            <div className="text-sm font-semibold" style={{ color: "#F5C842" }}>
              Verrouillage automatique désactivé
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Aucun PIN n&apos;est configuré. L&apos;application ne se verrouille
              pas automatiquement. Configurez un PIN pour activer cette
              protection sur votre poste de travail.
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div
          className="p-5 rounded-lg border border-vertec-borderDark flex flex-col gap-3"
          data-testid="pin-status-card"
        >
          <div className="flex items-center gap-2">
            {hasPin ? (
              <>
                <ShieldCheck size={18} className="text-status-success" />
                <span className="font-semibold text-status-success">PIN configuré</span>
              </>
            ) : (
              <>
                <ShieldOff size={18} className="text-muted-foreground" />
                <span className="font-semibold text-muted-foreground">Aucun PIN</span>
              </>
            )}
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {hasPin
              ? "Le verrouillage automatique est actif. Vous pouvez modifier votre code à tout moment."
              : "Créez un code PIN à 4 chiffres pour activer le verrouillage automatique."}
          </p>
          <div className="flex gap-2 mt-auto">
            <Button
              size="sm"
              onClick={() => setSetOpen(true)}
              className="gap-1.5 bg-vertec-blue hover:bg-vertec-blue/90 text-white"
              data-testid="pin-set-btn"
            >
              <KeyRound size={13} /> {hasPin ? "Modifier le PIN" : "Définir un PIN"}
            </Button>
            {hasPin && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRemoveOpen(true)}
                className="gap-1.5 border-status-danger/40 text-status-danger hover:bg-status-danger/10"
                data-testid="pin-remove-btn"
              >
                <ShieldOff size={13} /> Retirer
              </Button>
            )}
          </div>
        </div>

        <div className="p-5 rounded-lg border border-vertec-borderDark bg-black/20">
          <h3 className="text-sm font-semibold mb-2">Comment ça marche</h3>
          <ul className="text-xs text-muted-foreground space-y-1.5 leading-relaxed">
            <li>• Après <strong>10 min</strong> sans activité (souris, clavier, requêtes)&nbsp;: l&apos;écran se verrouille.</li>
            <li>• Cliquer sur <strong>« Bye bye »</strong> verrouille immédiatement.</li>
            <li>• Après <strong>3 tentatives</strong> erronées, blocage temporaire de 30&nbsp;secondes.</li>
            <li>• Le PIN est stocké de façon irréversible (bcrypt). Nous ne pouvons pas le récupérer.</li>
          </ul>
        </div>
      </div>

      <PinSetDialog
        open={setOpen}
        onOpenChange={setSetOpen}
        hasPin={hasPin}
        onDone={async () => { await refresh(); setSetOpen(false); }}
      />
      <PinRemoveDialog
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        onDone={async () => { await refresh(); setRemoveOpen(false); }}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Sub-dialogs
// ---------------------------------------------------------------------------
function PinSetDialog({ open, onOpenChange, hasPin, onDone }) {
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setCurrentPin(""); setNewPin(""); setConfirmPin("");
  };

  const valid =
    /^\d{4}$/.test(newPin) &&
    newPin === confirmPin &&
    (!hasPin || /^\d{4}$/.test(currentPin));

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await api.post("/me/lock-pin/set", { new_pin: newPin, current_pin: hasPin ? currentPin : null });
      toast.success("Code PIN enregistré", {
        description: "Le verrouillage automatique est maintenant actif",
      });
      reset();
      await onDone();
    } catch (err) {
      toast.error("Impossible d'enregistrer le PIN", {
        description: err.response?.data?.detail || "Réessayez.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-card border-vertec-borderDark" data-testid="pin-set-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound size={18} className="text-vertec-blue" />
            {hasPin ? "Modifier le PIN" : "Définir un PIN"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {hasPin && (
            <PinBoxes label="PIN actuel" value={currentPin} onChange={setCurrentPin} testid="pin-current" />
          )}
          <PinBoxes label="Nouveau PIN (4 chiffres)" value={newPin} onChange={setNewPin} testid="pin-new" />
          <PinBoxes label="Confirmer le nouveau PIN" value={confirmPin} onChange={setConfirmPin} testid="pin-confirm" />
          {confirmPin.length === 4 && newPin !== confirmPin && (
            <div className="text-xs text-status-danger">Les codes saisis ne correspondent pas.</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={!valid || busy}
            className="bg-vertec-blue hover:bg-vertec-blue/90 text-white disabled:opacity-40"
            data-testid="pin-set-confirm"
          >
            {busy ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PinRemoveDialog({ open, onOpenChange, onDone }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!/^\d{4}$/.test(pin) || busy) return;
    setBusy(true);
    try {
      await api.delete("/me/lock-pin", { data: { current_pin: pin } });
      toast.success("PIN retiré", { description: "Le verrouillage automatique est désactivé" });
      setPin("");
      await onDone();
    } catch (err) {
      toast.error("Impossible de retirer le PIN", {
        description: err.response?.data?.detail || "Réessayez.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <DeskConfirm open={open} danger icon={ShieldOff} title="Retirer le PIN ?" message="Le verrouillage automatique sera désactivé. Vous pourrez le réactiver à tout moment en redéfinissant un code."
      confirmLabel={busy ? "Suppression…" : "Retirer le PIN"} busy={busy} confirmDisabled={!/^\d{4}$/.test(pin)}
      onClose={() => { if (!busy) { setPin(""); onOpenChange(false); } }} onConfirm={submit} testId="pin-remove-dialog">
      <PinBoxes label="Confirmez avec votre PIN actuel" value={pin} onChange={setPin} testid="pin-remove" />
    </DeskConfirm>
  );
}

// Reusable 4-slot digit input.
function PinBoxes({ label, value, onChange, testid }) {
  const arr = value.padEnd(4).slice(0, 4).split("");
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</label>
      <Input
        type="password"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={4}
        value={value}
        onChange={(e) => onChange((e.target.value || "").replace(/\D/g, "").slice(0, 4))}
        className="mt-1.5 h-11 text-center text-xl font-mono tracking-widest"
        autoComplete="new-password"
        placeholder="––––"
        data-testid={testid}
      />
    </div>
  );
}
